import { cloneDeep, first } from 'lodash';
import { getters, commits, dispatches } from '@/modules/core/app/helpers/store';
import LayoutService from '@/modules/ta/dashboard/services/LayoutService';
import DashboardService from '@/modules/ta/dashboard/services/DashboardService';
import { WidgetBuilderConstants, WidgetState } from '@/modules/ta/widget/widget.constants';
import WidgetService from '@/modules/ta/widget/services/WidgetService';
import { WidgetFactory } from '@/modules/ta/widget/factories/WidgetFactory';
import { WidgetDataCache } from '@/modules/ta/widget/caches/WidgetDataCache';

export const actions = {
  // Regenerate the dashboard thumbnail when the first layout's contents change.
  // Single source of truth for the first-layout gating used by widget save,
  // resize and delete flows — the thumbnail only ever reflects the first section.
  updateDashboardThumbnailIfNeeded({}, layoutId) {
    const dashboard = getters.dashboard.getDashboard();
    if (!dashboard || layoutId == null) return;
    const orderedLayouts = getters.dashboard.getOrderedLayoutsInDashboard();
    if (orderedLayouts.length > 0 && orderedLayouts[0].id === layoutId) {
      DashboardService.updateThumbnail(dashboard.id);
    }
  },
  async addLayout({}, { layout }) {
    return commits.dashboardLayout.setLayout(layout);
  },
  addGhostWidget({}, { layout_id, widget }) {
    const existingGhost = getters.widget.getWidget(WidgetBuilderConstants.NEW_WIDGET_ID);
    const layout = getters.dashboardLayout.getLayout(layout_id);
    const ghostInLayout =
      layout?.widgets && Object.hasOwn(layout.widgets, WidgetBuilderConstants.NEW_WIDGET_ID);

    if (existingGhost || ghostInLayout) {
      commits.dashboardLayout.removeWidgetInLayout({
        layoutId: layout_id,
        widget: existingGhost || { id: WidgetBuilderConstants.NEW_WIDGET_ID },
      });
    }

    const builderWidget = WidgetFactory.build(widget);
    const orderedWidgets = getters.dashboardLayout.getOrderedWidgetsInLayout(layout_id);
    builderWidget.display_order = orderedWidgets?.length
      ? orderedWidgets.at(-1).display_order + 1
      : 1;
    builderWidget.layout_id = layout_id;

    WidgetDataCache.delete(builderWidget.id);

    const state =
      builderWidget.hasDataConfiguration() || !builderWidget.isDataSourcedWidget()
        ? WidgetState.STATE_DONE
        : WidgetState.STATE_INCOMPLETE_BUILDER_CONFIGURATION;

    commits.dashboardLayout.addGhostWidget({
      layoutId: layout_id,
      widget: builderWidget,
      state,
    });
  },
  addWidget({}, { widget, layoutId = null, state = WidgetState.STATE_NEEDS_RELOAD }) {
    commits.dashboardLayout.addWidget({
      layoutId: layoutId || widget.layout_id,
      widget,
      state,
    });
  },
  updateTitleAndOrder({}, updatedLayouts) {
    commits.dashboardLayout.updateTitleAndOrder(updatedLayouts);
  },
  updateDrawOptions({ state }, { drawOptions, layoutId }) {
    const clonedOptions = cloneDeep(drawOptions);
    const updatedLayout = state.layouts[layoutId];
    updatedLayout.metadata.draw_options = clonedOptions;
    commits.dashboardLayout.setLayout(updatedLayout);
  },
  async moveWidgetToLayout({}, { widgetId, layoutId }) {
    const [widget] = getters.widget.getWidgetsFromIds([widgetId]);
    const oldLayoutId = widget.layout_id;

    // hydrate if not already so we get an accurate display_order
    if (!getters.dashboard.isLayoutHydrated(layoutId)) {
      const moveToLayout = getters.dashboardLayout.getLayout(layoutId);
      await dispatches.dashboard.hydrateDashboardLayout(moveToLayout);
    }

    widget.layout_id = layoutId;
    const orderedWidgets = getters.dashboardLayout.getOrderedWidgetsInLayout(layoutId);
    widget.display_order = orderedWidgets?.length ? orderedWidgets.at(-1).display_order + 1 : 1;

    // saveItem expects widget content for media widget
    const saveWidget = cloneDeep(widget);
    saveWidget.metadata.content = widget.isMediaWidget() ? WidgetDataCache.get(widget.id) : null;
    dispatches.widget.saveItem(saveWidget);

    commits.dashboardLayout.removeWidgetInLayout({ layoutId: oldLayoutId, widget });
    commits.dashboardLayout.setWidgetInLayout({ layoutId, widget });

    // Either side of the move may have been the first section.
    dispatches.dashboardLayout.updateDashboardThumbnailIfNeeded(oldLayoutId);
    if (layoutId !== oldLayoutId) {
      dispatches.dashboardLayout.updateDashboardThumbnailIfNeeded(layoutId);
    }
  },
  async removeWidgetFromWidgetBuilder({}, { widgetId, layoutId }) {
    if (!getters.widgetBuilder.isEditing()) {
      const [widget] = getters.widget.getWidgetsFromIds([widgetId]);
      commits.dashboardLayout.removeWidgetInLayout({ layoutId, widget });
    }
  },
  async removeWidget({}, { widgetId, layoutId, passedWidget = null }) {
    let widget = passedWidget;
    if (!widget) {
      [widget] = getters.widget.getWidgetsFromIds([widgetId]);
    }
    await WidgetService.deleteWidget(widget);
    commits.dashboardLayout.removeWidgetInLayout({ layoutId, widget });
    dispatches.dashboardLayout.updateDashboardThumbnailIfNeeded(layoutId);
  },
  async duplicateWidget({}, { widgetId, layoutId = null, passedWidget = null }) {
    let widget = passedWidget;
    if (!widget) {
      widget = getters.widget.getWidget([widgetId]);
    }
    let newWidget = await WidgetService.copyItem(widget);
    newWidget = await WidgetService.getItem(newWidget.id);
    const targetLayoutId = layoutId || newWidget.layout_id;
    commits.dashboardLayout.addWidget({
      layoutId: targetLayoutId,
      widget: newWidget,
      isDuplicated: true,
    });
    dispatches.dashboardLayout.updateDashboardThumbnailIfNeeded(targetLayoutId);
  },
  /**
   * To create a new Layout.
   * @param {string|null} title - Optional custom title; defaults to "Section N" if null
   * @returns {Promise<void>}
   */
  async addNewLayout({}, title = null) {
    const dashboard = getters.dashboard.getDashboard();
    const layout = await LayoutService.addNewLayout(
      dashboard.id,
      getters.dashboard.getNextLayoutDisplayOrder(),
      {},
      title
    );
    commits.dashboard.addLayout({ layout });
    return layout;
  },

  async removeLayout({}, id) {
    await LayoutService.deleteItem(id);
    const dashboard = getters.dashboard.getDashboard();

    commits.dashboard.removeLayout(id);
    commits.dashboardLayout.removeLayout(id);

    if (id === getters.dashboard.getCurrentLayoutId()) {
      commits.dashboard.setCurrentLayoutId(first(Object.keys(dashboard.layouts)));
    }
  },

  /**
   * To create a layout from a section template.
   * @param layout
   * @param title
   * @returns {Promise<void>}
   */
  async useTemplateAsLayout({}, { layout, title }) {
    const dashboard = getters.dashboard.getDashboard();
    const newLayout = await LayoutService.useTemplateAsLayout(
      layout,
      title,
      dashboard.id,
      getters.dashboard.getNextLayoutDisplayOrder()
    );
    LayoutService.incrementDownloadCount(newLayout.id);

    await dispatches.widget.staggeredSetWidgetsFromLayout({ layout: newLayout });
    commits.dashboard.addLayout({ layout: newLayout });
    return newLayout;
  },
  /**
   * To set widgets from a layout.
   * @param layout
   */
  setWidgetsFromLayout({}, { layout }) {
    return dispatches.widget.staggeredSetWidgetsFromLayout({ layout });
  },
  updateDisplayOrders({}, widgetIdIndexMapArray) {
    commits.widget.updatePositions(widgetIdIndexMapArray);
  },
  setOriginalLayoutWidgets({}, layoutId) {
    const widgets = getters.dashboardLayout.getWidgetsInLayout(layoutId);
    commits.dashboardLayout.setOriginalLayoutWidgets(widgets);
  },
  applyOriginalLayoutWidgets({ state }) {
    commits.widget.updateWidgets(state.originalLayoutWidgets);
  },
  async onWidgetBuilderPostSave({}, { widget, layoutId = null }) {
    if (getters.widgetBuilder.isEditing()) {
      // Use mutation directly — getWidgetData already loaded fresh data in saveClicked,
      // so we only need to update the widget object, not invalidate the cache.
      commits.widget.updateWidget({ widget });
    } else {
      commits.dashboardLayout.removeWidgetInLayout({
        widget: { id: WidgetBuilderConstants.NEW_WIDGET_ID },
        layoutId: layoutId || widget.layout_id,
      });
      const existingState = getters.widget.getWidgetState(widget.id);
      dispatches.dashboardLayout.addWidget({ widget, state: existingState ?? undefined });
    }
  },
  async saveList({}, layouts) {
    commits.dashboardLayout.saveList(layouts);
    return LayoutService.saveList(layouts);
  },
};
